/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import moment from 'moment';
import { WithTranslation } from 'react-i18next';
import { i18n, LocaleId } from '../data/translation/i18n';
import { AxiosError } from 'axios';
import { ReactNode, useEffect, useRef } from 'react';
import { DocType, VatAmounts } from '../data/api/documents/documentsClient.typings';
import CryptoJS from 'crypto-js';
import { TaxAuthorityDTO } from '../data/api/shortLists/shortListsClient.typings';
import { ListTableColumnConfig } from '../components/RegistryWidgets/Common/ListTypes';
import { PARENT_DOC_LABEL_BY_TYPE } from '../data/translation/sharedLocales';
import { RecaptchaNotificationData, RecaptchaNotificationTypes } from './recaptcha';

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-z]{2,6}$/;
export const PHONE_MIN_DIGITS = 8;
export const PHONE_MAX_DIGITS = 15;
export const PHONE_COUNTRY = 'az';
export const PHONE_REGEX = new RegExp(`^\\+[1-9]\\d{${PHONE_MIN_DIGITS - 1},${PHONE_MAX_DIGITS - 1}}$`);
export const EMPTY_DATE_STRING = '-999999999-01-01T00:00:00';
export const INVALID_DATE = 'Invalid date';
export const DATE_TIME_WITH_MS_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';
export const UI_FULL_DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm:ss';
export const DATEPICKER_DATE_ONLY_FORMAT = 'YYYY-MM-DD';
export const DATEPICKER_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';
export const MAX_COMMENT_LENGTH = 2000;
export const ERROR_TYPES = [
    'email',
    'serverError',
    'emailOrPassword',
    'searchError',
    'listError',
    'emptyTextField',
    'updateError',
] as const;
export const MOMENT_INVALID_DATE = 'Invalid date';
export const ALL_OPTION = 'ALL';
export const DATA_UNAVAILABLE = 'UNAVAILABLE';
export const NO_NAME = 'No name';

export type ErrorType = (typeof ERROR_TYPES)[number];

export interface ErrorItem {
    errorType?: ErrorType;
    errorStatusCode?: number;
    errorMessage: string;
}

export const convertErrors = (errors: ErrorItem[]) => {
    return errors.reduce(
        (acc, curr) => {
            acc[curr.errorType] = curr;
            return acc;
        },
        {} as { [key: string]: ErrorItem },
    );
};

export const NOTIFICATION_TYPES = ['ERROR', 'NOTIFICATION', 'WARNING'] as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export interface Notification {
    message: string;
    type: NotificationType;
}

export const parseError = (
    err: AxiosError,
    customMessage?: string,
    showDetailedErrorMessage?: boolean,
): Notification => {
    let detailedMessage = '';
    if (showDetailedErrorMessage) {
        try {
            detailedMessage = (err.response?.data as any)?.message?.toString() || '';
        } catch (e) {
            /* empty */
        }
        if (detailedMessage.length !== 0) detailedMessage = ` (${detailedMessage})`;
    }

    const res = err.response;
    const notification: Notification = {
        message: '',
        type: NOTIFICATION_TYPES[0],
    };

    if (res && res.status) {
        if (res.status === 401) {
            notification.message = i18n.t('AuthScreen.errors.wrongEmailOrPassword');
            return notification;
        }

        if (res.status === 403) {
            if ((res.data as any)?.message === RecaptchaNotificationTypes.LOGIN_FORBIDDEN) {
                const notificationData: RecaptchaNotificationData = JSON.parse((res.data as any).error);
                if (notificationData.hasOwnProperty('ttl')) {
                    const leftTime = Math.ceil(notificationData.ttl / 60);
                    notification.message = i18n.t('AuthScreen.errors.rejectedByRecaptcha', { leftTime });
                    return notification;
                }
            }
            notification.message = `${i18n.t('MainScreen.errors.forbidden')} (${(res.data as any)?.message})`;
            return notification;
        }

        if (res.status === 400 || res.status > 403) {
            notification.message = customMessage ? customMessage + detailedMessage : i18n.t('errors.requestFailed');
            return notification;
        }

        if (res.status >= 500) {
            notification.message = i18n.t('errors.somethingWrong');
            return notification;
        }
    }

    if (customMessage) {
        notification.message = customMessage + detailedMessage;
        return notification;
    }

    notification.message = i18n.t('errors.requestFailed') + detailedMessage;
    return notification;
};

export const validateEmail = (email: string) => {
    return EMAIL_REGEX.test(email);
};

// Performs a country-agnostic E.164 shape check only; it does not validate country-specific numbering rules.
export const validatePhone = (phone: string) => {
    return PHONE_REGEX.test(phone);
};

export type StoredPhoneStatus = 'empty' | 'valid' | 'invalid' | 'wrongCountry';

export const getNormalizedPhone = (phone?: string) => {
    const trimmedPhone = phone?.trim() || '';
    return trimmedPhone.replace(/[\s()\-–—]/g, '');
};

export const getStoredPhoneStatus = (phone: string | undefined, dialCode: string): StoredPhoneStatus => {
    const normalizedPhone = getNormalizedPhone(phone);
    if (!normalizedPhone) return 'empty';
    if (!validatePhone(normalizedPhone)) return 'invalid';
    if (!normalizedPhone.startsWith(`+${dialCode}`)) return 'wrongCountry';
    return 'valid';
};

export function formatUnixDate(timestamp: number): string {
    return moment.unix(timestamp).format('DD.MM.YYYY');
}

export function formatUnixTime(timestamp: number, format?: string): string {
    return moment.unix(timestamp).format(format ? format : 'HH:mm:ss');
}

export function formatUnixDateTime(timestamp: number): string {
    if (timestamp === null || timestamp === undefined) return null;
    if (timestamp === 0) return '-';
    return moment.unix(timestamp).format('DD.MM.YYYY HH:mm:ss');
}

export const formatStringUtcDateTime = (
    dateTimeUtc: string,
    isStrict: boolean = false,
    format: string = UI_FULL_DATE_TIME_FORMAT, //'DD.MM.YYYY HH:mm:ss',
): string => {
    if (
        dateTimeUtc === null ||
        dateTimeUtc === undefined ||
        dateTimeUtc.trim() === '' ||
        dateTimeUtc === INVALID_DATE ||
        dateTimeUtc === EMPTY_DATE_STRING
    )
        return isStrict ? DATA_UNAVAILABLE : '-';
    return moment.utc(dateTimeUtc).local().format(format);
};

export function formatTimeStampUtcDateTime(timestamp: number, isStrict?: boolean, format?: string): string {
    if (timestamp === null || timestamp === undefined || timestamp === 0) return isStrict ? DATA_UNAVAILABLE : '-';

    if (String(timestamp).length < 13) timestamp = timestamp * 1000;

    return moment
        .utc(timestamp)
        .local()
        .format(format ? format : 'DD.MM.YYYY HH:mm:ss');
}

export const timeFromSeconds = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    return days > 0
        ? `${days}d - ${new Date(seconds * 1000).toISOString().substr(11, 8)}`
        : `${new Date(seconds * 1000).toISOString().substr(11, 8)}`;
};

export const translateRows = (
    rows: string[] | string,
    translatePath: string,
    t: WithTranslation['t'],
): string[] | string => {
    if (Array.isArray(rows)) {
        return rows.map((item: string) => {
            return t(`${translatePath}.${item}`);
        });
    }

    return t(`${translatePath}.${rows}`);
};

export const translateOrLeaveAzAndSortByProp = <T>(
    arr: T[],
    translationPath: string,
    key?: keyof T,
    lang?: LocaleId,
): T[] => {
    const translatedArr = arr.map((item) => {
        let value = key ? item[key] : (item as unknown as string);
        if (!value) value = '-';
        let translatedValue = '';
        if (i18n.exists(translationPath + value))
            translatedValue = i18n
                .t(translationPath + value, lang ? { lng: lang } : undefined)
                .toLocaleLowerCase(lang ? (lang as string) : undefined);
        else translatedValue = (value as string).toLocaleLowerCase(LocaleId.En);
        return { item, translatedValue };
    });
    translatedArr.sort((a, b) =>
        a.translatedValue.localeCompare(b.translatedValue, lang ? (lang as string) : undefined),
    );

    return translatedArr.map((translatedItem) => translatedItem.item);
};

export const sortRows = (rows: string[] | string) => {
    if (Array.isArray(rows)) {
        return rows.sort((a, b) => {
            return a.localeCompare(b);
        });
    }
    return rows;
};

export function sortByProp<T extends { [k in K]: string }, K extends keyof T>(
    items: T[],
    key: K,
    lang?: LocaleId,
): T[] {
    return items.slice(0).sort((a, b) => {
        const valueA = a[key] ? a[key].toLocaleLowerCase() : '';
        const valueB = b[key] ? b[key].toLocaleLowerCase() : '';

        return valueA.localeCompare(valueB, lang);
    });
}

export const sortVersions = <T>(arr: T[], key: keyof T, order: 'asc' | 'desc' = 'asc'): T[] => {
    return arr.sort((a, b) => {
        if (a[key] === b[key]) {
            return 0;
        }
        const splitA = (a[key] as string).split(/[.-]/);
        const splitB = (b[key] as string).split(/[.-]/);
        const length = Math.max(splitA.length, splitB.length);

        for (let i = 0; i < length; i++) {
            if (isNaN(+splitA[i]) && isNaN(+splitB[i]) && splitA[i] && splitB[i]) {
                if (splitA[i].toLowerCase() > splitB[i].toLowerCase()) {
                    return order === 'asc' ? 1 : -1;
                }
                if (splitA[i] < splitB[i]) {
                    return order === 'asc' ? -1 : 1;
                }
            }

            if (parseInt(splitA[i]) > parseInt(splitB[i])) {
                return order === 'asc' ? 1 : -1;
            }
            if (parseInt(splitA[i]) < parseInt(splitB[i])) {
                return order === 'asc' ? -1 : 1;
            }
        }
        return 0;
    });
};

export const sortVersionsStringArray = (arr: string[], order: 'asc' | 'desc' = 'desc'): string[] => {
    return arr.sort((a, b) => {
        if (a === b) {
            return 0;
        }
        const splitA = (a as string).split(/[.-]/);
        const splitB = (b as string).split(/[.-]/);
        const length = Math.max(splitA.length, splitB.length);

        for (let i = 0; i < length; i++) {
            if (isNaN(+splitA[i]) && isNaN(+splitB[i]) && splitA[i] && splitB[i]) {
                if (splitA[i] > splitB[i]) {
                    return order === 'asc' ? 1 : -1;
                }
                if (splitA[i] < splitB[i]) {
                    return order === 'asc' ? -1 : 1;
                }
            }

            if (parseInt(splitA[i]) > parseInt(splitB[i])) {
                return order === 'asc' ? 1 : -1;
            }
            if (parseInt(splitA[i]) < parseInt(splitB[i])) {
                return order === 'asc' ? -1 : 1;
            }
        }
        return 0;
    });
};

export const isV1GreaterOrEqualToV2 = (v1: string, v2: string): boolean | undefined => {
    if (!v1 || !v2) return undefined;
    if (v1 === v2) return true;

    const splitV1 = (v1 as string).split(/[.-]/);
    const splitV2 = (v2 as string).split(/[.-]/);
    const length = Math.max(splitV1.length, splitV2.length);

    for (let i = 0; i < length; i++) {
        const partV1 = splitV1[i] || '0';
        const partV2 = splitV2[i] || '0';
        if (isNaN(+partV1) && isNaN(+partV2)) {
            if (partV1.toLowerCase() > partV2.toLowerCase()) return true;
            if (partV1.toLowerCase() < partV2.toLowerCase()) return false;
        }
        if (parseInt(partV1) > parseInt(partV2)) return true;
        if (parseInt(partV1) < parseInt(partV2)) return false;
    }
    return undefined;
};

export const calculateVATSSum = (data: VatAmounts[]) => {
    return data
        .map((item) => {
            if (item.hasOwnProperty('vatPercent')) {
                return (item.vatPercent * item.vatSum) / (100 + item.vatPercent);
            }
            return 0;
        })
        .reduce((a, b) => a + b, 0)
        .toFixed(2);
};

export const calculateVATSSum2 = (data: VatAmounts[]) => {
    if (!data) return '0.00';
    return data
        .map((item) => {
            if (item.hasOwnProperty('vatPercent')) {
                return item.vatResult;
            }
            return 0;
        })
        .reduce((a, b) => a + b, 0)
        .toFixed(2);
};

export function updateItemInArray<T>(arr: T[], newItem: T, idKey: keyof T): T[] {
    return arr.map((item) => {
        if (item[idKey] === newItem[idKey]) {
            return newItem;
        } else {
            return item;
        }
    });
}

export function downloadDataAsFile(fileName: string, data: ArrayBuffer): void {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
        window.URL.revokeObjectURL(url);
    }, 60 * 1000);
}

export async function downloadFileByUrl(fileName: string, url: string): Promise<void> {
    const response = await fetch(url);
    if (response.status !== 200) {
        throw new Error(`Unable to download file. HTTP status: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = fileName;

    document.body.appendChild(downloadLink);
    downloadLink.click();

    setTimeout(() => {
        URL.revokeObjectURL(downloadLink.href);
        document.body.removeChild(downloadLink);
    }, 60 * 1000);
}

const abbrevByLocaleId: Record<LocaleId, [string, string, string, string, string, string, string, string]> = {
    ru: ['к', 'M', 'Г', 'T', 'П', 'Э', 'З', 'И'],
    en: ['k', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y'],
    az: ['k', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y'],
};

export const formatNumbers = (number: number, decPlaces: number, localeId?: LocaleId): string => {
    decPlaces = Math.pow(10, decPlaces);

    const abbrev = !localeId ? ['k', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y'] : abbrevByLocaleId[localeId];
    let prefabricated: string = '';

    for (let i = abbrev.length - 1; i >= 0; i--) {
        const size = Math.pow(10, (i + 1) * 3);

        if (size <= number) {
            number = Math.round((number * decPlaces) / size) / decPlaces;
            prefabricated = `${number}${abbrev[i]}`;
            break;
        }
    }

    return prefabricated;
};

export function cutEmptyParams<
    T extends { [k in K]?: string | number | string[] | number[] | boolean | object },
    K extends keyof T,
>(params: T): T {
    const currentParams: T = params;

    Object.keys(currentParams).forEach(function (key: unknown) {
        (currentParams[key as keyof T] === '' || currentParams[key as keyof T] === null) &&
            delete currentParams[key as keyof T];
    });
    return currentParams;
}

export function cutEmptyParamsIncludingArrays<
    T extends { [k in K]?: string | number | string[] | number[] | boolean | object },
    K extends keyof T,
>(params: T): T {
    const currentParams: T = params;

    Object.keys(currentParams).forEach(function (key: unknown) {
        (currentParams[key as keyof T] === '' ||
            currentParams[key as keyof T] === null ||
            (Array.isArray(currentParams[key as keyof T]) &&
                (currentParams[key as keyof T] as undefined[]).length === 0)) &&
            delete currentParams[key as keyof T];
    });
    return currentParams;
}

export const createLinkFromHostName = (hostname: string): string => {
    return `www.${hostname.split('.').slice(1).join('.')}`;
};

export const calculateTotalPages = (count: number, limit: number) => {
    return Math.ceil(count / limit);
};

export function convertToFormData<T extends object>(obj: T): FormData {
    const formData = new FormData();
    Object.keys(obj).forEach((key) => {
        formData.append(key, (obj as Record<string, string>)[key]);
    });
    return formData;
}

export const checkValue = (value: string | number | Array<number | string> | ReactNode) => {
    let viewValue: string;

    if (Array.isArray(value)) {
        if (value.length > 0) {
            viewValue = value.join(', ');
        } else {
            viewValue = '-';
        }
    } else if (Number.isFinite(value as number)) {
        viewValue = value.toString();
    } else if (value === '' || value === INVALID_DATE || value === EMPTY_DATE_STRING) {
        viewValue = '-';
    } else {
        viewValue = value as string;
    }

    if (!viewValue) {
        viewValue = DATA_UNAVAILABLE;
    }

    return viewValue;
};

export const checkArrayValueAndTranslate = (data: unknown, translationPath: string, t: WithTranslation['t']) => {
    let viewValue: string;
    if (!data || !Array.isArray(data)) {
        viewValue = DATA_UNAVAILABLE;
    } else {
        if (data.length > 0) {
            viewValue = data
                .map((item) => {
                    return t(translationPath + item);
                })
                .join(', ');
        } else {
            viewValue = '-';
        }
    }
    return viewValue;
};

export const checkArrayOfObjectsWithName = (data: { name: string }[], isStrict: boolean = false) => {
    if (data && data.length !== 0) {
        return data.map((item) => {
            return item.name;
        });
    } else {
        return isStrict ? DATA_UNAVAILABLE : '-';
    }
};

export const checkBooleanValueAndTranslate = (data: unknown, t: WithTranslation['t']) => {
    let viewValue: string;
    if (data === null || data === undefined || typeof data !== 'boolean') {
        viewValue = DATA_UNAVAILABLE;
    } else {
        if (data === true) {
            viewValue = t('app.yes');
        } else {
            viewValue = t('app.no');
        }
    }
    return viewValue;
};

export const checkStringValueAndTranslate = (
    data: unknown,
    translationPath: string,
    t: WithTranslation['t'],
    isStrict: boolean = false,
) => {
    let viewValue: string;
    if (data === null || typeof data !== 'string') {
        viewValue = DATA_UNAVAILABLE;
    } else {
        if (isStrict) {
            viewValue =
                data.length > 0 && i18n.exists(translationPath + data) ? t(translationPath + data) : DATA_UNAVAILABLE;
        } else if (data.length > 0) {
            viewValue = t(translationPath + data);
        } else {
            viewValue = '-';
        }
    }
    return viewValue;
};

export const checkAndTranslateOrLeaveAz = (
    data: string,
    translationPath: string,
    t: WithTranslation['t'],
    isStrict: boolean = false,
) => {
    if (!data) return isStrict ? DATA_UNAVAILABLE : '-';
    if (i18n.exists(translationPath + data)) {
        const value = t(translationPath + data);
        return value[0] + value.slice(1).toLocaleLowerCase();
    } else {
        return data[0] + data.slice(1).toLocaleLowerCase(LocaleId.En);
    }
};

export const getTotalByObjValues = (data: object, removedValues?: string[]) => {
    const removeProp =
        (prop: string) =>
        ({ [prop]: _, ...rest }) =>
            rest;
    let newData = data ? data : {};
    if (removedValues && data) {
        let removedValue;
        removedValues.forEach((item) => {
            removedValue = removeProp(item);
            newData = removedValue(newData);
        });
    }
    return Object.values(newData).reduce((a, b) => a + b, 0);
};

export function getTotalByKeys<T extends object>(data: T, keys?: string[]): number {
    if (!keys) keys = Object.keys(data);
    return keys.reduce((total, key) => {
        const dataType = typeof data[key as keyof T];
        if (dataType === 'number' || dataType === 'string') {
            total += Number(data[key as keyof T]);
        }
        return total;
    }, 0);
}

export const secondsToHumanStr = (seconds: number, t: WithTranslation['t']): string => {
    const year = 31536000;
    const day = 86400;
    const hour = 3600;
    const minute = 60;
    let returnText = '';

    const absSeconds = Math.abs(seconds);
    const sign = seconds < 0 ? '-' : '';

    const levels: [number, string][] = [
        [Math.floor(absSeconds / year), t('app.timePeriodsShort.year')],
        [Math.floor((absSeconds % year) / day), t('app.timePeriodsShort.day')],
        [Math.floor(((absSeconds % year) % day) / hour), t('app.timePeriodsShort.hour')],
        [Math.floor((((absSeconds % year) % day) % hour) / minute), t('app.timePeriodsShort.minute')],
        [(((absSeconds % year) % day) % hour) % minute, t('app.timePeriodsShort.second')],
    ];

    for (let i = 0, max = levels.length; i < max; i++) {
        if (levels[i][0] === 0) {
            continue;
        }
        returnText += ' ' + sign + levels[i][0] + levels[i][1];
    }

    return returnText.trim();
};

export const fromNowToDateInSeconds = (date: string): string => {
    const local = moment.utc(date).local().format();
    return ((new Date(Date.now()).getTime() - new Date(local).getTime()) / 1000).toFixed(0);
};

export const daysToHumanStr = (value: number, t: WithTranslation['t']): string => {
    if (value === null || value === undefined) return null;

    if (value === 0) return `0${t('app.timePeriodsShort.day')}`;

    const year = 365;
    let returnText = '';

    const levels: [number, string][] = [
        [Math.floor(value / year), t('app.timePeriodsShort.year')],
        [Math.floor(value % year), t('app.timePeriodsShort.day')],
    ];

    for (let i = 0, max = levels.length; i < max; i++) {
        if (levels[i][0] === 0) {
            continue;
        }
        returnText += ' ' + levels[i][0] + levels[i][1];
    }

    return returnText.trim();
};

export const bigValueToHumanStr = (value: number): string => {
    if (value === null || value === undefined) return null;
    return value.toLocaleString('ru-RU');
};

export const toRoman = (n: number) => {
    if (isNaN(n)) {
        return NaN;
    }

    const digits = String(+n).split('');
    const key = [
        '',
        'C',
        'CC',
        'CCC',
        'CD',
        'D',
        'DC',
        'DCC',
        'DCCC',
        'CM',
        '',
        'X',
        'XX',
        'XXX',
        'XL',
        'L',
        'LX',
        'LXX',
        'LXXX',
        'XC',
        '',
        'I',
        'II',
        'III',
        'IV',
        'V',
        'VI',
        'VII',
        'VIII',
        'IX',
    ];
    let roman = '',
        i = 3;

    while (i--) {
        roman = (key[+digits.pop() + i * 10] || '') + roman;
    }

    return Array(+digits.join('') + 1).join('M') + roman;
};

export function usePrevious<T>(value: T): T | undefined {
    const ref = useRef<T | undefined>(undefined);

    useEffect(() => {
        ref.current = value;
    });

    return ref.current;
}

export const MAX_SEARCH_LENGTH = 1500;
export const COMPANY_TAX_NUMBER_LENGTH = 10;

export const REGEXP_PARTIAL_COMPANY_TAX_NUMBER: RegExp = /^\d{1,10}$/;
export const REGEXP_COMPANY_TAX_NUMBER: RegExp = /^\d{10}$/;

export const REGEXP_PARTIAL_STORE_TAX_NUMBER: RegExp = /(?=\d{0,10})^\d{0,10}-?\d{0,7}$/;
export const REGEXP_STORE_TAX_NUMBER_VAR1: RegExp = /^\d{10}-\d{5}$/;
export const REGEXP_STORE_TAX_NUMBER_VAR2: RegExp = /^\d{10}-\d{7}$/;

export const REGEXP_PARTIAL_CASHBOX_TAX_NUMBER: RegExp = /^\w{1,10}$/;
export const REGEXP_CASHBOX_TAX_NUMBER: RegExp = /^\w{10}$/;

export const REGEXP_PARTIAL_RRN: RegExp = /^[a-zA-Z0-9]{1,16}$/;
export const REGEXP_RRN: RegExp = /^[a-zA-Z0-9]{8,16}$/;

export const REGEXP_PARTIAL_DOCUMENT_ID: RegExp = /^[A-HJ-NP-Za-km-z1-9]{1,46}$/;
export const REGEXP_DOCUMENT_ID: RegExp = /^[A-HJ-NP-Za-km-z1-9]{42,46}$/;

export const validateSearchFilterValueForMaxLength = (value: string, t: WithTranslation['t']): string => {
    let errorMessage = '';
    if (value && value !== '') {
        if (value.length > MAX_SEARCH_LENGTH) {
            errorMessage = `${t('app.hints.searchFieldLengthMoreThan', { number: MAX_SEARCH_LENGTH })} `;
        }
    }
    return errorMessage;
};

export const validateSearchFilterValueForFormat = (
    value: string,
    t: WithTranslation['t'],
    errorTranslationPath: string,
    partialRegExp: RegExp,
    fullMatchRegExp: RegExp,
    fullMatchRegExpOption?: RegExp,
): string => {
    let errorMessage = '';
    if (value && value !== '') {
        const valueAsArray = makeArrayFromCommaSeparatedString(value, true);

        if (valueAsArray.length === 0) {
            errorMessage += `${t('errors.noMeaningfulValue')} `;
        } else {
            if (valueAsArray.length === 1) {
                if (!partialRegExp.test(valueAsArray[0])) errorMessage += `${t(errorTranslationPath)} `;
            } else {
                for (let i = 0; i < valueAsArray.length; i++) {
                    if (
                        !fullMatchRegExp.test(valueAsArray[i]) &&
                        (fullMatchRegExpOption ? !fullMatchRegExpOption.test(valueAsArray[i]) : true)
                    ) {
                        errorMessage += `${t(errorTranslationPath)} `;
                        break;
                    }
                }
            }
        }
    }
    return errorMessage;
};

export const makeArrayFromCommaSeparatedString = (str: string, isUnique: boolean): string[] => {
    if (!str || str.length === 0) return [];

    if (isUnique) {
        const uniqueValues = new Set<string>();
        str.split(',').forEach((item) => {
            const value = item.trim();
            if (value) uniqueValues.add(value);
        });
        return Array.from(uniqueValues);
    } else return str.split(',');
};

export const arrayUniqueStr = (arr: string[]) => arr.filter((e, i, a) => a.indexOf(e) === i);

export const MIN_VALUE_LENGTH_FOR_TOOLTIP = 20;

export const getTimeDeltaString = (timeDelta: number, t: WithTranslation['t']): string => {
    if (timeDelta) {
        const delta = Math.round(Number(timeDelta) / 60);
        return ` (${delta > 0 ? '+' : ''}${delta} ${t('app.timePeriodsShort.minute')})`;
    } else return '';
};

export function getRandomIntInclusive(min: number, max: number) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1) + min);
}

export const MIN_PASSWORD_LENGTH = 12;
export const MAX_PASSWORD_LENGTH = 32;

const GENERATED_PASSWORD_LENGTH = 15;
const REGEXP_USER_PASSWORD_STRENGTH_CHECK: RegExp =
    /^(?=.*\p{Ll})(?=.*\p{Lu})(?=.*\d)(?=.*[!@"#№$;%^:&?*()_\-+={}[\]|/\\',<>~`]).{12,32}$/u;
const REGEXP_LOWER_CASE_LETTER: RegExp = /(?=.*\p{Ll})./u;
const REGEXP_UPPER_CASE_LETTER: RegExp = /(?=.*\p{Lu})./u;
const REGEXP_NUMBER: RegExp = /(?=.*\d)./u;
const REGEXP_SPEC_SYMBOL: RegExp = /(?=.*[!@"#№$;%^:&?*()_\-+={}[\]|/\\',<>~`])./u;

export const generateRandomPassword = (): string => {
    let password = '';
    const symbols = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@"#№$;%^:&*()_-+={}[]|/\\\',<>~`?';
    for (let i = 0; i < GENERATED_PASSWORD_LENGTH; i++) {
        password += symbols.charAt(Math.floor(Math.random() * symbols.length));
    }
    if (!REGEXP_UPPER_CASE_LETTER.test(password))
        password += symbols.charAt(getRandomIntInclusive(symbols.indexOf('A'), symbols.indexOf('Z')));
    if (!REGEXP_LOWER_CASE_LETTER.test(password))
        password += symbols.charAt(getRandomIntInclusive(symbols.indexOf('a'), symbols.indexOf('z')));
    if (!REGEXP_NUMBER.test(password))
        password += symbols.charAt(getRandomIntInclusive(symbols.indexOf('0'), symbols.indexOf('9')));
    if (!REGEXP_SPEC_SYMBOL.test(password))
        password += symbols.charAt(getRandomIntInclusive(symbols.indexOf('!'), symbols.indexOf('?')));

    return password;
};

export const validatePassword = (value: string): boolean => {
    return REGEXP_USER_PASSWORD_STRENGTH_CHECK.test(value);
};

export const validateStringNotEmpty = (value: string) => {
    if (!value) return false;
    else {
        return value.trim() !== '';
    }
};

export const validateArrayNotEmpty = (values: string[]) => {
    if (!values) return false;
    else {
        return values.length !== 0;
    }
};

export const validateComment = (value: string) => {
    if (validateStringNotEmpty(value) && value.trim().length <= MAX_COMMENT_LENGTH) return true;
    return false;
};

export const areScalarArraysEqual = (array1: Array<undefined>, array2: Array<undefined>) => {
    const array2Sorted = array2.slice().sort();
    return (
        array1.length === array2.length &&
        array1
            .slice()
            .sort()
            .every((value, index) => value === array2Sorted[index])
    );
};

export const getPageLimitByScreenSize = () => {
    const innerHeight = window.innerHeight;

    if (innerHeight > 970 && innerHeight <= 1100) {
        return 15;
    }
    if (innerHeight > 1100 && innerHeight <= 1300) {
        return 20;
    }
    if (innerHeight > 1300 && innerHeight <= 1500) {
        return 25;
    }
    if (innerHeight > 1500 && innerHeight <= 1600) {
        return 30;
    }
    if (innerHeight > 1600) {
        return 35;
    }
    return 10;
};

export const toHash = async (value: string): Promise<string> => {
    const hashDigest = CryptoJS.SHA256(value);
    return hashDigest.toString(CryptoJS.enc.Hex);
};

export const getTaxAuthorityNameById = (taxAuthorityList: TaxAuthorityDTO[], id: string): string => {
    if (!taxAuthorityList) return null;
    const taxAuthority = taxAuthorityList.find((item) => item.id.toString() === id);
    if (taxAuthority) return taxAuthority.name;
    else return null;
};

export const formatBytes = (b: number) => {
    const units = ['bytes', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let l = 0;
    let n = b;

    while (n >= 1024) {
        n /= 1024;
        l += 1;
    }

    return `${n.toFixed(n >= 10 || l < 1 ? 0 : 1)}${units[l]}`;
};

export const formatDuration = (seconds: number) => {
    const date = new Date(0);
    date.setSeconds(seconds);
    const dateString = date.toISOString().slice(11, 19);
    if (seconds < 3600) return dateString.slice(3);
    return dateString;
};

export const isVersionV1LaterOrEqualV2 = (v1: string, v2: string) => {
    if (!!v1 && !!v2) {
        const v1Items = v1.split('.');
        const v2Items = v2.split('.');
        if (v1Items.length !== 3 || v2Items.length !== 3) return false;

        let item1 = parseInt(v1Items[0]);
        let item2 = parseInt(v2Items[0]);
        if (isNaN(item1) || isNaN(item2)) return false;
        if (item1 > item2) return true;
        if (item1 < item2) return false;

        item1 = parseInt(v1Items[1]);
        item2 = parseInt(v2Items[1]);
        if (isNaN(item1) || isNaN(item2)) return false;
        if (item1 > item2) return true;
        if (item1 < item2) return false;

        item1 = parseInt(v1Items[2].split('-')[0]);
        item2 = parseInt(v2Items[2].split('-')[0]);
        if (isNaN(item1) || isNaN(item2)) return false;
        if (item1 >= item2) return true;
        else return false;
    } else return false;
};

export function filterConfigToOmitKeys<T extends object>(
    config: ListTableColumnConfig<T>[],
    keys: (keyof T)[],
): ListTableColumnConfig<T>[] {
    return config.filter((item) => !keys.includes(item.keyForValue));
}

export const getParentIdLabelTranslatePath = (parentDocType: DocType, isPlural?: boolean) => {
    if (parentDocType === null || parentDocType === undefined) return 'document.parentFiscalIdLabels.unknown';

    if (isPlural && parentDocType === DocType.Prepayment) return 'document.parentFiscalIdLabels.prepayFiscalIds';

    if (Object.keys(PARENT_DOC_LABEL_BY_TYPE).includes(parentDocType.toString()))
        return PARENT_DOC_LABEL_BY_TYPE[parentDocType];

    return 'document.parentFiscalIdLabels.unknown';
};

export const parseBooleanString = (value: string): boolean => {
    return value.toLowerCase() === 'true';
};

export const getCsrfToken = () => {
    const cookies = document.cookie;
    const cookieArray = cookies.split('; ');

    for (const cookie of cookieArray) {
        const [name, value] = cookie.split('=');
        if (name === 'x-csrf-token') {
            return decodeURIComponent(value).split('|')[0];
        }
    }

    return null;
};

export const getFullUserName = (firstName?: string, lastName?: string): string => {
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

    return fullName || NO_NAME;
};

export const checkUserName = (
    user: {
        firstName?: string;
        lastName?: string;
    } | null,
) => {
    if (!user) return DATA_UNAVAILABLE;

    const firstName = user.firstName?.trim() || '';
    const lastName = user.lastName?.trim() || '';
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || DATA_UNAVAILABLE;
};
